import { StyledGridCol, StyledGridRow } from "@/common/styles";
import { useImperativeHandle, forwardRef, useEffect, useState } from "react";
import Select from "@/common/elements/InputElements/Select";
import CommonDatePicker from "@/common/elements/DatePicker";
import { FieldSecurityProvider } from "@/contexts/FieldSecurityContext";
import SecureFormItem from "@/common/SecureFormItem";
import Checkbox from "antd/es/checkbox/Checkbox";
import {
  debounce,
  getOptionsFromRefList,
  isNonEmptyVariable,
} from "@/util/common_function";
import { useSelector, useDispatch } from "react-redux";
import { Form } from "antd";
import dayjs from "dayjs";
import { setBookings } from "@/slices/bookingsSlice";

const ScheduledBookingInformation = forwardRef(({ scheduledBookingRef, startingPoint }, ref) => {
  const dispatch = useDispatch();
  const { bookings, bookingsFieldsEnabled, isScheduledBooking } = useSelector(
    (state) => state?.bookings
  );

  const [form] = Form.useForm();
  const [formData, setFormData] = useState(null);

  // -----------------------------------------------------------------------
  // Sync bookings → local formData (with dayjs conversions)
  // -----------------------------------------------------------------------
  useEffect(() => {
    if (bookings !== null) {
      if (bookings?.arrestReports?.[0]) {
        setFormData({
          scheduledBooking: bookings.arrestReports[0].scheduledBooking,
          scheduledReleaseDateTime: isNonEmptyVariable(
            bookings.arrestReports[0].scheduledReleaseDate
          )
            ? dayjs(
                `${bookings.arrestReports[0].scheduledReleaseDate || ""} ${
                  bookings.arrestReports[0].scheduledReleaseTime || ""
                }`
              )
            : null,
          housingAgency: bookings.arrestReports[0].housingAgency || "",
          scheduledBookingCleared: bookings.arrestReports[0].scheduledBookingCleared,
          scheduledBookingClearanceReasonId:
            bookings.arrestReports[0].scheduledBookingClearanceReasonId,
          scheduledBookingClearanceDateTime: isNonEmptyVariable(
            bookings.arrestReports[0].scheduledBookingClearanceDate
          )
            ? dayjs(
                `${bookings.arrestReports[0].scheduledBookingClearanceDate || ""} ${
                  bookings.arrestReports[0].scheduledBookingClearanceTime || ""
                }`
              )
            : dayjs(),
        });
      }
      if (isScheduledBooking && !bookings?.arrestReports?.[0]?.scheduledBooking) {
        setFormData({ scheduledBooking: true });
      }
    } else {
      setFormData(null);
    }
  }, [bookings]);

  // Sync local formData → antd form
  useEffect(() => {
    if (formData !== null) {
      form.setFieldsValue(formData);
    } else {
      form.resetFields();
    }
  }, [formData]);

  // Watch conditional visibility fields
  const scheduledBooking = Form.useWatch("scheduledBooking", form);
  const scheduledBookingCleared = Form.useWatch("scheduledBookingCleared", form);

  // -----------------------------------------------------------------------
  // Expose form API to parent via ref
  // -----------------------------------------------------------------------
  useImperativeHandle(scheduledBookingRef, () => ({
    submitForm: () => form.submit(),
    resetForm: () => form.resetFields(),
    getFormValues: () => form.getFieldsValue(),
    getFieldsError: () => form.getFieldsError(),
    isFieldsValidating: () => form.isFieldsValidating(),
    validateFields: () => form.validateFields(),
  }));

  // -----------------------------------------------------------------------
  // Debounced form → Redux sync
  // -----------------------------------------------------------------------
  const dataModify = debounce(() => {
    const values = form.getFieldsValue();

    values.housingAgency = values?.housingAgency || "";
    values.scheduledReleaseDateTime = values?.scheduledReleaseDateTime
      ? dayjs(values.scheduledReleaseDateTime).format("MM/DD/YYYY HH:mm")
      : null;
    values.scheduledBookingClearanceDateTime =
      values?.scheduledBookingClearanceDateTime
        ? dayjs(values.scheduledBookingClearanceDateTime).format("MM/DD/YYYY HH:mm")
        : null;

    const data = {
      ...values,
      scheduledReleaseDate: values?.scheduledReleaseDateTime?.split(" ")[0],
      scheduledReleaseTime: values?.scheduledReleaseDateTime?.split(" ")[1],
      scheduledBookingClearanceDate:
        values?.scheduledBookingClearanceDateTime?.split(" ")[0],
      scheduledBookingClearanceTime:
        values?.scheduledBookingClearanceDateTime?.split(" ")[1],
    };

    delete data.scheduledBookingClearanceDateTime;
    delete data.scheduledReleaseDateTime;

    dispatch(
      setBookings({
        arrestReports: [{ ...bookings?.arrestReports?.[0], ...data }],
      })
    );
  }, 300);

  // -----------------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------------
  return (
    <FieldSecurityProvider
      entity="ArrestReports"
      startingPoint={startingPoint}
      bookingsFieldsEnabled={bookingsFieldsEnabled}
    >
      <Form
        form={form}
        initialValues={formData}
        disabled={!bookingsFieldsEnabled}
        onFieldsChange={dataModify}
      >
        <StyledGridRow gutter={24}>

          {/* Scheduled Booking? */}
          <StyledGridCol lg={6}>
            <SecureFormItem
              fieldName="scheduledBooking"
              formItemProps={{
                name: "scheduledBooking",
                label: "Scheduled Booking?",
                layout: "horizontal",
                labelPostion: "label-right",
                valuePropName: "checked",
              }}
            >
              <Checkbox />
            </SecureFormItem>
          </StyledGridCol>

          {scheduledBooking && (
            <>
              {/* Scheduled Report Date And Time */}
              <StyledGridCol lg={10}>
                <SecureFormItem
                  fieldName="scheduledReleaseDateTime"
                  formItemProps={{
                    name: "scheduledReleaseDateTime",
                    label: "Scheduled Report Date And Time",
                    rules: [
                      {
                        required: true,
                        message:
                          "If this is a scheduled booking, you must enter the scheduled report date.",
                      },
                    ],
                  }}
                >
                  <CommonDatePicker
                    format="MM/DD/YYYY HH:mm"
                    placeholder="Select Date and Time"
                  />
                </SecureFormItem>
              </StyledGridCol>

              {/* Facility */}
              <StyledGridCol lg={6}>
                <SecureFormItem
                  fieldName="housingAgency"
                  formItemProps={{
                    name: "housingAgency",
                    label: "Facility",
                    rules: [
                      {
                        required: true,
                        message: "You must select the facility.",
                      },
                    ],
                  }}
                >
                  <Select
                    options={getOptionsFromRefList("Housing_Agency", false, false, true)}
                    placeholder="Facility"
                    showSearch
                    variant="outlined"
                  />
                </SecureFormItem>
              </StyledGridCol>

              <StyledGridCol xs={24}>
                <StyledGridRow gutter={24}>

                  {/* Cleared? */}
                  <StyledGridCol lg={6}>
                    <SecureFormItem
                      fieldName="scheduledBookingCleared"
                      formItemProps={{
                        name: "scheduledBookingCleared",
                        label: "Cleared?",
                        layout: "horizontal",
                        labelPostion: "label-right",
                        valuePropName: "checked",
                      }}
                    >
                      <Checkbox />
                    </SecureFormItem>
                  </StyledGridCol>

                  {scheduledBookingCleared && (
                    <>
                      {/* Clearance Reason */}
                      <StyledGridCol lg={6}>
                        <SecureFormItem
                          fieldName="scheduledBookingClearanceReasonId"
                          formItemProps={{
                            name: "scheduledBookingClearanceReasonId",
                            label: "Clearance Reason",
                            rules: [
                              {
                                required: true,
                                message:
                                  "If this scheduled booking has been cleared, you must enter the reason.",
                              },
                            ],
                          }}
                        >
                          <Select
                            id="clearanceReason"
                            options={getOptionsFromRefList(
                              "Scheduled_Booking_Clearance_Reason",
                              false,
                              false,
                              true
                            )}
                            placeholder="Clearance Reason"
                            showSearch={false}
                            variant="outlined"
                          />
                        </SecureFormItem>
                      </StyledGridCol>

                      {/* Clearance Date/Time */}
                      <StyledGridCol lg={10}>
                        <SecureFormItem
                          fieldName="scheduledBookingClearanceDateTime"
                          formItemProps={{
                            name: "scheduledBookingClearanceDateTime",
                            label: "Clearance Date/Time",
                            initialValue: dayjs(),
                          }}
                        >
                          <CommonDatePicker
                            format="MM/DD/YYYY HH:mm"
                            placeholder="Select Date and Time"
                            value={
                              form.getFieldValue("scheduledBookingClearanceDateTime") ||
                              dayjs()
                            }
                            onChange={(date) =>
                              form.setFieldsValue({
                                scheduledBookingClearanceDateTime: date,
                              })
                            }
                          />
                        </SecureFormItem>
                      </StyledGridCol>
                    </>
                  )}

                </StyledGridRow>
              </StyledGridCol>
            </>
          )}

        </StyledGridRow>
      </Form>
    </FieldSecurityProvider>
  );
});

export default ScheduledBookingInformation;